// Accessibility (A11y) Implementation for Card Component
//
// This component follows SlopeSync accessibility standards (see ACCESSIBILITY.md):
// - Use semantic <section> or <article> for Card (consumer should provide role if needed)
// - CardTitle should use heading tags (h2/h3) for proper hierarchy (consumer can override as needed)
// - CardAction should contain only interactive elements (e.g., <button>, <a>) for keyboard and screen reader support
// - Ensure visible focus indicators for any interactive content
// - CardDescription and CardContent should be associated with CardTitle via aria-labelledby/aria-describedby if needed
// - High color contrast and clear structure
// - See ACCESSIBILITY.md for more
import * as React from 'react'

import { cn } from '@/lib/utils'

function Card({
  className,
  titleId,
  ...props
}: React.ComponentProps<'section'> & { titleId?: string }) {
  return (
    <section
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col gap-6 rounded-xl border shadow-sm',
        className
      )}
      aria-labelledby={titleId}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] ',
        className
      )}
      {...props}
    />
  )
}

function CardTitle({
  className,
  id,
  children,
  ...props
}: { children: React.ReactNode } & React.ComponentProps<'h2'>) {
  return (
    <h2
      data-slot="card-title"
      className={cn('leading-none font-semibold', className)}
      id={id}
      {...props}
    >
      {children}
    </h2>
  )
}

function CardDescription({
  className,
  id,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-muted-foreground text-sm', className)}
      id={id}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    >
      {' '}
    </div>
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-content" className={cn('p-4', className)} {...props} />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
